import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  TableColumn,
  PaginationEvent,
  SortEvent,
} from '../data-table/data-table.component';
import {
  formatCurrency,
  formatNumber,
} from '@shared/utils/date-formatter.util';

@Component({
  selector: 'advanced-data-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    PaginatorModule,
    CheckboxModule,
    TooltipModule,
    TranslateModule,
    ButtonModule,
    AppButtonComponent,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    DatePickerModule,
  ],
  templateUrl: './advanced-data-table.component.html',
  styleUrl: './advanced-data-table.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedDataTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  public readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @Input() dataSource: any[] = [];

  @Input() defaultColumns: TableColumn[] = [];
  @Input() flexibleColumns: TableColumn[] = [];
  /** Full column order (default + flexible). When set, panel list and toggle order use this so flexible columns can appear in the middle. */
  @Input() referenceColumnOrder: TableColumn[] = [];

  @Input() rows = 20;
  @Input() first = 0;
  @Input() totalRecords = 0;
  @Input() loading = false;
  @Input() scrollHeight = '65vh';
  @Input() rowsPerPageOptions: number[] = [20, 50, 100];
  @Input() freezeActions = false;
  /** Optional CSS class(es) applied to each body row. */
  @Input() rowClass?: (rowData: any) => string;

  @Input() hasCheckColumn = false;
  @Input() hasHeaderCheckbox = true;
  @Input() hasSerialColumn = false;
  @Input() showInternalColumnConfigButton = true;

  @Output() pageChange = new EventEmitter<PaginationEvent>();
  @Output() sortChange = new EventEmitter<SortEvent>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() displayedColumnsChange = new EventEmitter<TableColumn[]>();

  @ContentChild('statusTemplate') statusTemplate?: TemplateRef<any>;
  @ContentChild('actionsTemplate') contentActionsTemplate?: TemplateRef<any>;
  @ContentChild('customTemplate') customTemplate?: TemplateRef<any>;

  @Input() actionsTemplate?: TemplateRef<any>;
  @Input() getFieldError?: (
    rowIndex: number,
    fieldName: string,
  ) => string | null;

  @ViewChild('tableContainer') tableContainer!: ElementRef;
  @ViewChild('controlsContainer') controlsContainer?: ElementRef;
  @ViewChild('columnPanel') columnPanel?: ElementRef;

  displayedColumns: TableColumn[] = [];

  // internal state
  selectedItems: any[] = [];
  goToPageNumber = 1;
  isAllSelected = false;
  showColumnPanel = false;
  panelButtonElement: HTMLElement | null = null;
  panelPositioned = false;

  private previousDataSource: any[] = [];

  // External selection binding to keep parity with app-data-table API
  @Input() set selection(value: any[]) {
    this.selectedItems = Array.isArray(value) ? value : [];
    this.updateSelectAllState();
  }

  ngOnInit(): void {
    this.initDisplayedColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultColumns'] && !changes['defaultColumns'].firstChange) {
      // Always sync displayed columns to incoming defaults to prevent stale state
      this.displayedColumns = [...this.defaultColumns];
      // Always include actions column if it exists in panelColumns (default or flexible)
      this.ensureActionsColumnIncluded();
      this.ensureActionsLast();
      this.emitDisplayedColumns();
    }

    if (changes['flexibleColumns'] && !changes['flexibleColumns'].firstChange) {
      // When flexibleColumns change, ensure actions column is included if it exists
      this.ensureActionsColumnIncluded();
      this.ensureActionsLast();
      this.emitDisplayedColumns();
    }

    if (changes['dataSource']) {
      const curr = Array.isArray(this.dataSource) ? this.dataSource : [];
      const refChanged = curr !== this.previousDataSource;
      if (refChanged) {
        this.previousDataSource = curr.length ? [...curr] : [];
        this.cdr.markForCheck();
        setTimeout(() => this.setupTooltips(), 100);
      }
    }

    if (changes['rows'] || changes['first'] || changes['totalRecords']) {
      this.setupPaginatorTestIds();
    }
  }

  ngAfterViewInit(): void {
    this.setupTooltips();
    this.setupPaginatorTestIds();
    // Reposition panel if it's open after view init
    if (this.showColumnPanel) {
      setTimeout(() => this.positionPanel(), 0);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showColumnPanel) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    // Check if click is outside the panel and the triggering button
    const panelEl = this.columnPanel?.nativeElement as HTMLElement | undefined;
    const isClickInPanel = panelEl?.contains(target);
    const isClickInButton = this.panelButtonElement?.contains(target);

    if (!isClickInPanel && !isClickInButton) {
      this.showColumnPanel = false;
      this.panelButtonElement = null;
      this.panelPositioned = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.showColumnPanel) {
      this.positionPanel();
    }
  }

  // Effective freezeActions: only freeze when there's data
  get effectiveFreezeActions(): boolean {
    return this.freezeActions && this.dataSource.length > 0;
  }

  // Derived columns: split vs unified depending on effectiveFreezeActions
  get scrollableColumns(): TableColumn[] {
    return this.effectiveFreezeActions
      ? this.displayedColumns.filter((c) => c.type !== 'actions')
      : this.displayedColumns;
  }

  get frozenRightColumns(): TableColumn[] {
    if (!this.effectiveFreezeActions) return [];
    const actions = this.displayedColumns.filter((c) => c.type === 'actions');
    return actions.length ? [actions[0]] : [];
  }

  get frozenWidth(): string {
    if (!this.effectiveFreezeActions) return '0px';
    const total = this.frozenRightColumns.reduce(
      (sum, c) => sum + this.getColumnWidth(c),
      0,
    );
    // Fallback minimal width to avoid collapse when actions arrives later
    const fallback = 120; // default actions width
    return (total || fallback) + 'px';
  }

  // Column Selection Panel – when referenceColumnOrder is set, use it for panel list order
  get panelColumns(): TableColumn[] {
    if (this.referenceColumnOrder?.length) {
      return [...this.referenceColumnOrder];
    }
    const map = new Map<string, TableColumn>();
    (this.defaultColumns || []).forEach((c) => map.set(c.prop, c));
    (this.flexibleColumns || []).forEach((c) => {
      if (!map.has(c.prop)) map.set(c.prop, c);
    });
    return Array.from(map.values());
  }

  /**
   * Get all columns in their reference order (default + flexible + actions).
   * When referenceColumnOrder is set, use it so flexible columns appear in that order when toggled.
   */
  private getReferenceColumnOrder(): TableColumn[] {
    if (this.referenceColumnOrder?.length) {
      const ordered = [...this.referenceColumnOrder];
      const actionsCol = ordered.find((c) => c.type === 'actions');
      if (actionsCol) {
        const idx = ordered.indexOf(actionsCol);
        ordered.splice(idx, 1);
        ordered.push(actionsCol);
      }
      return ordered;
    }

    const ordered: TableColumn[] = [];
    const seen = new Set<string>();

    // Add default columns in order
    (this.defaultColumns || []).forEach((c) => {
      if (!seen.has(c.prop)) {
        ordered.push(c);
        seen.add(c.prop);
      }
    });

    // Add flexible columns in order
    (this.flexibleColumns || []).forEach((c) => {
      if (!seen.has(c.prop)) {
        ordered.push(c);
        seen.add(c.prop);
      }
    });

    // Actions column should always be at the end
    const actionsCol = ordered.find((c) => c.type === 'actions');
    if (actionsCol) {
      const idx = ordered.indexOf(actionsCol);
      ordered.splice(idx, 1);
      ordered.push(actionsCol);
    }

    return ordered;
  }

  // Column Selection UI helpers
  isColumnChecked(col: TableColumn): boolean {
    return this.displayedColumns.some((c) => c.prop === col.prop);
  }

  toggleColumn(col: TableColumn, checked: boolean): void {
    if (col.type === 'actions') {
      // Actions column visibility is fixed; ignore toggles
      return;
    }
    if (checked) {
      if (!this.isColumnChecked(col)) {
        // Add the column first, then reorder
        this.displayedColumns = [...this.displayedColumns, col];
        // Rebuild displayedColumns based on reference order
        this.reorderDisplayedColumns();
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(
        (c) => c.prop !== col.prop,
      );
    }
    // Ensure only one actions column remains if any
    const actions = this.displayedColumns.filter((c) => c.type === 'actions');
    if (actions.length > 1) {
      const keep = actions[0].prop;
      this.displayedColumns = this.displayedColumns.filter(
        (c) => c.type !== 'actions' || c.prop === keep,
      );
    }

    // Keep actions at the end
    this.ensureActionsLast();

    this.emitDisplayedColumns();
    // Re-setup tooltips after structure change
    setTimeout(() => this.setupTooltips(), 100);
  }

  /**
   * Reorder displayedColumns based on reference order
   * This ensures columns maintain their original position when toggled
   */
  private reorderDisplayedColumns(): void {
    const referenceOrder = this.getReferenceColumnOrder();
    const displayedProps = new Set(this.displayedColumns.map((c) => c.prop));
    const reordered: TableColumn[] = [];

    // Add columns in reference order, but only if they're currently displayed
    // Use the reference column object to ensure consistency
    for (const refCol of referenceOrder) {
      if (displayedProps.has(refCol.prop)) {
        reordered.push(refCol);
      }
    }

    // Add any columns that are displayed but not in reference order (shouldn't happen)
    // This is a fallback for edge cases
    for (const displayedCol of this.displayedColumns) {
      if (
        !reordered.some((c) => c.prop === displayedCol.prop) &&
        displayedCol.type !== 'actions'
      ) {
        reordered.push(displayedCol);
      }
    }

    this.displayedColumns = reordered;
  }

  private initDisplayedColumns(): void {
    this.displayedColumns = [...this.defaultColumns];
    // Always include actions column if it exists in panelColumns (default or flexible)
    this.ensureActionsColumnIncluded();
    this.ensureActionsLast();
    this.emitDisplayedColumns();
  }

  // Helper method to ensure actions column is always included if it exists in panelColumns
  private ensureActionsColumnIncluded(): void {
    const actionsInPanel = this.panelColumns.find((c) => c.type === 'actions');
    if (actionsInPanel) {
      const actionsInDisplayed = this.displayedColumns.find(
        (c) => c.type === 'actions',
      );
      if (!actionsInDisplayed) {
        this.displayedColumns = [...this.displayedColumns, actionsInPanel];
      }
    }
  }

  private isDisplayedOnlyDefaults(
    displayed: TableColumn[],
    prevDefaults: TableColumn[],
  ): boolean {
    const dProps = displayed
      .map((c) => c.prop)
      .sort((a, b) => a.localeCompare(b));
    const pProps = (prevDefaults || [])
      .map((c) => c.prop)
      .sort((a, b) => a.localeCompare(b));
    return (
      dProps.length === pProps.length && dProps.every((p, i) => p === pProps[i])
    );
  }

  private emitDisplayedColumns(): void {
    this.displayedColumnsChange.emit(this.displayedColumns);
  }

  // Ensure actions column stays as the last column in the table
  private ensureActionsLast(): void {
    const idx = this.displayedColumns.findIndex((c) => c.type === 'actions');
    if (idx > -1 && idx !== this.displayedColumns.length - 1) {
      const [actionsCol] = this.displayedColumns.splice(idx, 1);
      this.displayedColumns = [...this.displayedColumns, actionsCol];
    }
  }

  // Tooltip setup copied from base component
  private setupTooltips(): void {
    setTimeout(() => {
      this.addTooltipsToEllipsedCells();
    }, 100);
  }

  private addTooltipsToEllipsedCells(): void {
    if (!this.tableContainer) return;
    const cells =
      this.tableContainer.nativeElement.querySelectorAll('.truncate-cell');
    cells.forEach((cell: HTMLElement) => {
      if (this.isTextEllipsed(cell)) {
        const text = cell.textContent?.trim() || '';
        if (text) {
          cell.setAttribute('pTooltip', text);
          cell.setAttribute('tooltipPosition', 'top');
          cell.setAttribute('tooltipStyleClass', 'custom-tooltip');
        }
      }
    });
  }

  private isTextEllipsed(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  // Tooltips visibility rules — show for any non-empty text cell so truncated
  // values (e.g. order codes in narrow columns) remain readable on hover.
  shouldShowTooltip(text: any, column: TableColumn): boolean {
    if (
      text === null ||
      text === undefined ||
      text === '' ||
      column.type === 'actions' ||
      column.type === 'status' ||
      column.type === 'custom' ||
      column.type === 'date' ||
      column.prop === 'durationDisplay'
    ) {
      return false;
    }
    if (typeof text === 'string') {
      return text.trim().length > 0;
    }
    return typeof text === 'number' || typeof text === 'boolean';
  }

  // Width logic reused
  getColumnWidth(column: TableColumn): number {
    if (column.type === 'actions') {
      return column.width || 180;
    }
    if (column.type === 'status') {
      return Math.max(column.width || 0, 120);
    }
    return column.width || 120;
  }

  // Options translate helper
  getTranslatedOptions(
    options?: { label: string; value: any }[],
  ): { label: string; value: any }[] {
    if (!options || !Array.isArray(options)) return [];
    return options.map((option) => ({
      ...option,
      label: this.translateService.instant(option.label),
    }));
  }

  // Pagination
  get maxPage(): number {
    return Math.ceil(this.totalRecords / this.rows) || 1;
  }

  onPageChange(event: PaginationEvent) {
    this.pageChange.emit(event);
    this.setupPaginatorTestIds();
  }

  private setupPaginatorTestIds(): void {
    setTimeout(() => {
      const host = this.elementRef.nativeElement as HTMLElement;
      const paginator = host.querySelector('p-paginator') as HTMLElement | null;
      if (!paginator) return;

      const buttonTestIds: Record<string, string> = {
        '.p-paginator-first': 'data-table-paginator-first-page',
        '.p-paginator-prev': 'data-table-paginator-previous-page',
        '.p-paginator-next': 'data-table-paginator-next-page',
        '.p-paginator-last': 'data-table-paginator-last-page',
      };

      Object.entries(buttonTestIds).forEach(([selector, testId]) => {
        paginator
          .querySelector(selector)
          ?.setAttribute('data-testid', testId);
      });

      Array.from(
        paginator.querySelectorAll(
          '.p-paginator-page',
        ) as NodeListOf<HTMLElement>,
      ).forEach((button: HTMLElement, index: number) => {
        button.setAttribute(
          'data-testid',
          `data-table-paginator-n-${index + 1}`,
        );
      });
    }, 0);
  }

  onGoToPage() {
    if (this.goToPageNumber < 1) this.goToPageNumber = 1;
    else if (this.goToPageNumber > this.maxPage)
      this.goToPageNumber = this.maxPage;

    const newFirst = (this.goToPageNumber - 1) * this.rows;
    this.pageChange.emit({
      first: newFirst,
      rows: this.rows,
      page: this.goToPageNumber - 1,
      pageCount: this.maxPage,
    });
  }

  // Sorting
  @Input() sortField?: string;
  @Input() sortOrder?: 1 | -1;

  onSort(column: TableColumn) {
    if (!column.sortable) return;
    let newOrder: 1 | -1 = 1;
    if (this.sortField === column.prop) {
      newOrder = this.sortOrder === 1 ? -1 : 1;
    }
    this.sortChange.emit({ field: column.prop, order: newOrder });
  }

  // Selection logic (internal state)
  onSelectionChange(event: any, rowData: any) {
    if (event.checked) {
      const already = rowData?.id
        ? this.selectedItems.some((i) => i && i.id === rowData.id)
        : this.selectedItems.includes(rowData);
      if (!already) this.selectedItems = [...this.selectedItems, rowData];
    } else if (rowData?.id) {
      this.selectedItems = this.selectedItems.filter(
        (i) => !i || i.id !== rowData.id,
      );
    } else {
      this.selectedItems = this.selectedItems.filter((i) => i !== rowData);
    }
    this.updateSelectAllState();
    this.selectionChange.emit(this.selectedItems);
  }

  onSelectionAll() {
    this.isAllSelected = !this.isAllSelected;
    if (this.isAllSelected) {
      this.selectedItems = [...this.dataSource];
    } else {
      this.selectedItems = [];
    }
    this.updateSelectAllState();
    this.selectionChange.emit(this.selectedItems);
  }

  isRowSelected(rowData: any): boolean {
    if (rowData?.id) {
      return this.selectedItems.some((i) => i && i.id === rowData.id);
    }
    return this.selectedItems.includes(rowData);
  }

  private updateSelectAllState(): void {
    const selectableRows = this.dataSource;
    if (selectableRows.length === 0) {
      this.isAllSelected = false;
    } else {
      const selectedSelectableRows = selectableRows.filter((row) =>
        this.selectedItems.some((selectedItem) => {
          if (row?.id && selectedItem?.id) {
            return (
              row.id === selectedItem.id ||
              String(row.id) === String(selectedItem.id)
            );
          }
          return row === selectedItem;
        }),
      );
      this.isAllSelected =
        selectedSelectableRows.length === selectableRows.length &&
        selectableRows.length > 0;
    }
  }

  // Cell editing & formatting reuse
  onCellValueChange(rowData: any, prop: string, value: any): void {
    rowData[prop] = value;
  }

  onInputChange(event: Event, rowData: any, prop: string): void {
    const value = (event.target as HTMLInputElement).value;
    rowData[prop] = value;
  }

  onBlurChange(rowData: any, prop: string): void {
    const value = rowData[prop];
    this.onCellValueChange(rowData, prop, value);
  }

  onInputNumberChange(value: any, rowData: any, prop: string): void {
    rowData[prop] = value;
  }

  onInputNumberBlur(event: any, rowData: any, prop: string): void {
    const currentValue = rowData[prop];
    let numericValue: any = null;
    if (
      currentValue !== null &&
      currentValue !== undefined &&
      currentValue !== ''
    ) {
      const parsedValue = Number(currentValue);
      if (!Number.isNaN(parsedValue)) numericValue = parsedValue;
    }
    rowData[prop] = numericValue;
    this.onCellValueChange(rowData, prop, numericValue);
  }

  onInputCellClick(
    event: Event,
    rowIndex: number,
    prop: string,
    inputType: 'inputText' | 'inputNumber',
  ): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const isDirectInputClick =
      target.tagName === 'INPUT' || target.closest('input');
    if (!isDirectInputClick) {
      event.preventDefault();
      const inputId = `input-${rowIndex}-${prop}`;
      const inputElement = document.getElementById(inputId) as HTMLInputElement;
      if (inputElement) {
        requestAnimationFrame(() => inputElement.focus());
      } else if (inputType === 'inputNumber') {
        const inputNumberComponent = (event.target as HTMLElement).closest(
          'p-inputnumber',
        );
        if (inputNumberComponent) {
          requestAnimationFrame(() => {
            const input = inputNumberComponent.querySelector(
              'input',
            ) as HTMLInputElement;
            if (input) input.focus();
          });
        }
      }
    }
  }

  // Formatting
  formatDate(dateValue: any): string {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  }

  formatCurrency(value: any): string {
    return formatCurrency(value);
  }

  formatNumber(value: any): string {
    return formatNumber(value);
  }

  // Utilities
  getEmptyColspan(): number {
    return (
      this.scrollableColumns.length +
      (this.hasCheckColumn ? 1 : 0) +
      (this.hasSerialColumn ? 1 : 0)
    );
  }

  /**
   * Handle internal button click
   */
  onInternalonClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual p-button element (PrimeNG button)
    const buttonElement =
      (target.closest('p-button')?.querySelector('button') as HTMLElement) ||
      (target.closest('button') as HTMLElement) ||
      target;
    this.openColumnSelector(buttonElement);
  }

  /**
   * Public method to open the column selector overlay
   * Can be called from external components (e.g., buttons in app-search-form)
   * @param buttonElement Optional button element to position panel relative to
   */
  openColumnSelector(buttonElement?: HTMLElement): void {
    if (this.showColumnPanel && buttonElement === this.panelButtonElement) {
      // Close if clicking the same button
      this.showColumnPanel = false;
      this.panelButtonElement = null;
      this.panelPositioned = false;
    } else {
      // Reset positioned flag and store button reference
      this.panelPositioned = false;
      this.panelButtonElement = buttonElement || null;
      // Open panel (will be hidden until positioned)
      this.showColumnPanel = true;
      // Position panel after view update, then make visible
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.positionPanel();
        });
      });
    }
  }

  /**
   * Attempt to compute panel position from a DOM element.
   * Returns null if the element is absent or has no dimensions.
   */
  private tryPositionFromElement(
    element: HTMLElement | null | undefined,
  ): { top: string; right: string } | null {
    if (!element) return null;
    try {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return {
          top: `${rect.bottom + 4}px`,
          right: `${window.innerWidth - rect.right}px`,
        };
      }
    } catch (e) {
      console.warn('Error getting element rect:', e);
    }
    return null;
  }

  /**
   * Position the column panel relative to the triggering button
   */
  private positionPanel(): void {
    if (!this.showColumnPanel) return;

    if (!this.columnPanel) {
      requestAnimationFrame(() => this.positionPanel());
      return;
    }

    const panelEl = this.columnPanel.nativeElement as HTMLElement;
    if (!panelEl) {
      requestAnimationFrame(() => this.positionPanel());
      return;
    }

    const position =
      this.tryPositionFromElement(this.panelButtonElement) ??
      this.tryPositionFromElement(this.controlsContainer?.nativeElement);

    panelEl.style.position = 'fixed';
    panelEl.style.top = position?.top ?? '-9999px';
    panelEl.style.right = position?.right ?? '0px';
    panelEl.style.left = 'auto';
    panelEl.style.bottom = 'auto';

    if (position) {
      requestAnimationFrame(() => {
        this.panelPositioned = true;
        this.cdr.markForCheck();
      });
    } else {
      setTimeout(() => this.positionPanel(), 50);
    }
  }
}
